import { apiBaseUrl, apiClient } from "./client";

function toImageUrl(imageValue) {
  if (!imageValue) {
    return "";
  }

  if (/^https?:\/\//i.test(imageValue)) {
    return imageValue;
  }

  return `${apiBaseUrl}/${String(imageValue).replace(/^\/+/, "")}`;
}

function getProductImageValue(product) {
  return (
    product.ImageUrl ||
    product.ImageURL ||
    product.imageUrl ||
    product.imageURL ||
    product.Image ||
    product.image ||
    product.ProductImage ||
    product.productImage ||
    product.ProductImageUrl ||
    product.ProductImageURL ||
    product.productImageUrl ||
    product.productImageURL ||
    ""
  );
}

function getProductImagesValue(product) {
  const images = product.Images || product.images || product.ProductImages || product.productImages || [];
  return Array.isArray(images) ? images : [];
}

function getDefaultUnitUom(price, unitPerCarton = 1) {
  return {
    uomId: 2,
    uomDesc: "UNIT",
    unitPerCarton: Number(unitPerCarton || 1),
    price: Number(price || 0),
  };
}

function getAbsoluteApiUrl(endpoint) {
  return `${apiBaseUrl}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`;
}

function logProductApiFailure(endpoint, error, responseData) {
  if (error?.name === "CanceledError" || error?.code === "ERR_CANCELED") {
    return;
  }

  console.error("Product API failed", {
    url: getAbsoluteApiUrl(endpoint),
    status: error?.response?.status,
    response: responseData || error?.response?.data,
    message: error?.message,
  });
}

function normalizeProduct(product, index) {
  const productPrice = Number(product.Price || 0);
  const productUnitPerCarton = Number(product.UnitPerCarton ?? product.UnitsPerCarton ?? product.unitPerCarton ?? 1);
  const dynamicUom = product.DynamicUOM || product.dynamicUOM || [];
  const normalizedUoms = dynamicUom.map((uom) => ({
    uomId: Number(uom.UOMID ?? uom.uomId ?? uom.UomID ?? 0),
    uomDesc: uom.UOMDesc || uom.uomDesc || "",
    unitPerCarton: Number(uom.QuantityPerUnit ?? uom.UnitPerCarton ?? uom.unitPerCarton ?? 1),
    price: Number(uom.Price ?? uom.price ?? 0),
  }));
  const hasMultipleUoms = normalizedUoms.length > 1;
  const defaultUnitUom = getDefaultUnitUom(productPrice, productUnitPerCarton);
  const selectedUom = hasMultipleUoms
    ? normalizedUoms.find((uom) => uom.uomId === defaultUnitUom.uomId) || normalizedUoms[0]
    : defaultUnitUom;

  return {
    productId: product.Id ?? product.id ?? `product-${index}`,
    productName: product.Name || "Unnamed product",
    productCode: product.ProductCode || "",
    description: product.Description || "",
    description2: product.Description2 || "",
    description3: product.Description3 || "",
    price: selectedUom.price || Number(product.Price || 0),
    imageUrl: toImageUrl(getProductImageValue(product)),
    images: getProductImagesValue(product)
      .map((image) => (typeof image === "string" ? image : getProductImageValue(image)))
      .map(toImageUrl)
      .filter(Boolean),
    categoryId: product.CategoryID,
    categoryName: product.CategoryName || "",
    mainCategoryId: product.MainCategoryID,
    mainCategoryName: product.MainCategoryName || "",
    stockQuantity: Number(product.StockQuantity || 0),
    warehouse: product.Warehouse || "",
    isAvailable: product.IsAvailable ?? true,
    uomId: selectedUom.uomId,
    uomDesc: selectedUom.uomDesc,
    unitPerCarton: selectedUom.unitPerCarton || 1,
    dynamicUOM: hasMultipleUoms ? normalizedUoms : [defaultUnitUom],
    selectedUOM: selectedUom,
  };
}

export async function getProducts(page = 1, pageSize = 20, search = "", signal) {
  const endpoint = process.env.REACT_APP_PRODUCTS_ENDPOINT || "/api/Products/GetGuestProducts";
  let response;

  try {
    response = await apiClient.get(endpoint, {
      params: { page, pageSize, search },
      signal,
    });
  } catch (error) {
    logProductApiFailure(endpoint, error);
    throw error;
  }

  const data = response.data || {};
  const isSuccess = String(data.ResponseCode || "") === "200";
  const result = data.Result || {};

  if (!isSuccess) {
    logProductApiFailure(endpoint, new Error(data.Message || "Products request failed."), data);
    throw new Error(data.Message || "Products request failed.");
  }

  return {
    items: (result.Items || []).map(normalizeProduct),
    page: Number(result.Page ?? page),
    pageSize: Number(result.PageSize ?? pageSize),
    totalItems: Number(result.TotalCount ?? 0),
    totalPages: Number(result.TotalPages ?? 1),
  };
}

export async function getProductDetail(productId, warehouse = "", signal) {
  const endpoint =
    process.env.REACT_APP_PRODUCT_DETAIL_ENDPOINT || "/api/Products/GetGuestProductDetail";
  let response;

  try {
    response = await apiClient.get(endpoint, {
      params: { productId, warehouse },
      signal,
    });
  } catch (error) {
    logProductApiFailure(endpoint, error);
    throw error;
  }

  const data = response.data || {};
  console.log("detail API response", data);
  if (String(data.ResponseCode || "") !== "200") {
    logProductApiFailure(endpoint, new Error(data.Message || "Product detail request failed."), data);
    throw new Error(data.Message || "Product detail request failed.");
  }

  return data.Result ? normalizeProduct(data.Result, 0) : null;
}

export async function getCategories({ warehouse = "", showSubOnly = false, signal } = {}) {
  const endpoint = process.env.REACT_APP_CATEGORIES_ENDPOINT || "/api/Products/GetGuestCategories";
  let response;

  try {
    response = await apiClient.get(endpoint, {
      params: { warehouse, showSubOnly },
      signal,
    });
  } catch (error) {
    logProductApiFailure(endpoint, error);
    throw error;
  }

  const data = response.data || {};
  if (String(data.ResponseCode || "") !== "200") {
    logProductApiFailure(endpoint, new Error(data.Message || "Categories request failed."), data);
    throw new Error(data.Message || "Categories request failed.");
  }

  return data.Result || [];
}

export async function getGuestPickupLocations(signal) {
  const endpoint =
    process.env.REACT_APP_PICKUP_LOCATIONS_ENDPOINT || "/api/Products/GetGuestPickupLocations";
  let response;

  try {
    response = await apiClient.get(endpoint, { signal });
  } catch (error) {
    logProductApiFailure(endpoint, error);
    throw error;
  }

  const data = response.data || {};
  if (String(data.ResponseCode || "") !== "200") {
    logProductApiFailure(endpoint, new Error(data.Message || "Pickup locations request failed."), data);
    throw new Error(data.Message || "Pickup locations request failed.");
  }

  return (data.Result || []).map((item) => {
    const warehouse = item.Warehouse || item.warehouse || "";
    return {
      pickupId: item.PickupID ?? item.pickupId ?? item.ID ?? item.id,
      pickupName: item.PickupName || item.pickupName || "Pickup location",
      pickupAddress: item.PickupAddress || item.pickupAddress || "",
      warehouse,
      warehouseCode: warehouse,
      warehouseName: item.WarehouseName || item.warehouseName || warehouse,
      warehouseAddress: item.WarehouseAddress || item.warehouseAddress || "",
    };
  });
}

export async function getProductsByCategory(
  categoryId,
  { page = 1, pageSize = 20, search = "", warehouse = "", signal } = {}
) {
  const endpoint =
    process.env.REACT_APP_PRODUCTS_BY_CATEGORY_ENDPOINT ||
    "/api/Products/GetGuestProductsByCategory";
  let response;

  try {
    response = await apiClient.get(endpoint, {
      params: { categoryId, page, pageSize, search, warehouse },
      signal,
    });
  } catch (error) {
    logProductApiFailure(endpoint, error);
    throw error;
  }

  const data = response.data || {};
  const result = data.Result || {};
  if (String(data.ResponseCode || "") !== "200") {
    logProductApiFailure(endpoint, new Error(data.Message || "Category products request failed."), data);
    throw new Error(data.Message || "Category products request failed.");
  }

  return {
    items: (result.Items || []).map(normalizeProduct),
    page: Number(result.Page ?? page),
    pageSize: Number(result.PageSize ?? pageSize),
    totalItems: Number(result.TotalCount ?? 0),
    totalPages: Number(result.TotalPages ?? 1),
  };
}
